import './ScheduleStepContent.scss';

import { IconAlertInfoOutlined } from '@hulu-react-style-components/icons';
import { bem } from 'mission-control-common-components';
import React from 'react';

import Alert, { AlertType } from '../../../../../../common/Alert';
import Button from '../../../../../../common/Button';
import Tabs from '../../../../../../common/Tabs';
import Tooltip from '../../../../../../common/Tooltip';
import {
  CALCULATE_BUTTON_LABEL,
  CANNOT_CALCULATE_VALUES_DURING_LOADING_MESSAGE,
  CUSTOM_PACING_INFO_TOOLTIPS,
  HEADER_CUSTOM_PACING_SCHEDULER_TITLE,
  TIMEZONE_INPUT_LABEL,
} from '../../constants';
import CustomPacingScheduleHeader from '../../CustomPacingScheduleHeader';
import DefaultContentWrapper from '../../DefaultContentWrapper';
import { useCalculateReport } from '../../hooks/useCalculateReport';
import { useSaveCustomPacing } from '../../hooks/useSaveCustomPacing';
import ReportPreview from '../ReportPreview';
import ScheduleForm from '../ScheduleForm';
import Summary from '../Summary';
import TimezoneDropdown from '../TimezoneDropdown';

const [block, element] = bem('schedule-step-content');

const TABS_OPTIONS = [
  {
    title: 'Summary',
    component: <Summary />,
  },
  {
    title: 'Report Preview',
    component: <ReportPreview />,
  },
];

export type ScheduleStepContentProps = {
  lineItemIdList: string[];
};

const ScheduleStepContent = ({ lineItemIdList }: ScheduleStepContentProps): React.JSX.Element => {
  const {
    canCalculate,
    isCalculating,
    hasCalculatedReport,
    calculationError,
    hasLineItemsReports,
    calculate,
  } = useCalculateReport(lineItemIdList);

  const { handleSaveCustomPacing, isLoading: isSavingCustomPacing } = useSaveCustomPacing();

  const isConfirmButtonDisabled =
    !hasLineItemsReports || !hasCalculatedReport || isSavingCustomPacing || canCalculate || !!calculationError;

  return (
    <div data-testid="set-cp-schedule-step">
      <CustomPacingScheduleHeader
        confirmButtonDisabled={isConfirmButtonDisabled}
        onConfirm={handleSaveCustomPacing}
        isLoading={isSavingCustomPacing}
        title={HEADER_CUSTOM_PACING_SCHEDULER_TITLE}
      />
      <DefaultContentWrapper>
        <div className={block()}>
          <ScheduleForm isDisabled={isCalculating} />
          <div className={element('timezone-container')}>
            <div className={element('timezone-label')}>
              {TIMEZONE_INPUT_LABEL}
              <Tooltip
                id="schedule-timezone-tooltip"
                message={CUSTOM_PACING_INFO_TOOLTIPS.SCHEDULE_TIMEZONE}
                place="top"
              >
                <IconAlertInfoOutlined
                  className="info-icon"
                  aria-label="schedule-timezone-info-icon"
                  data-testid="schedule-timezone-info-icon"
                />
              </Tooltip>
            </div>
            <TimezoneDropdown isDisabled={isCalculating} />
          </div>
          <div className={element('calculate-wrapper')}>
            <Tooltip
              id={'disable-calculate-button-tooltip'}
              message={CANNOT_CALCULATE_VALUES_DURING_LOADING_MESSAGE}
              disable={!isSavingCustomPacing}
            >
              <Button
                variant="normal"
                size="medium"
                onClick={calculate}
                className={element('calculate-button')}
                data-testid="calculate-button"
                disabled={!canCalculate || isSavingCustomPacing}
                loading={isCalculating}
              >
                <span>{CALCULATE_BUTTON_LABEL}</span>
              </Button>
            </Tooltip>
            <Tooltip id="calculate-info-tooltip" message={CUSTOM_PACING_INFO_TOOLTIPS.CALCULATE} place="top">
              <IconAlertInfoOutlined
                className="info-icon"
                aria-label="calculate-info-icon"
                data-testid="calculate-info-icon"
              />
            </Tooltip>
          </div>

          {calculationError && (
            <div className={element('alert-container')}>
              <Alert type={AlertType.ERROR} bodyText={calculationError} isInline />
            </div>
          )}

          {hasCalculatedReport && (
            <div className={element('tabs-container')}>
              <Tabs tabs={TABS_OPTIONS} />
            </div>
          )}
        </div>
      </DefaultContentWrapper>
    </div>
  );
};

export default ScheduleStepContent;
